import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../../context/AuthContext';
import { useNightMode } from '../../../contexts/NightModeContext';
import { useNavTitle } from '../../../contexts/NavTitleContext';
import { images } from '../../../assets/images';
import { 
  Calendar, 
  PlusCircle, 
  Users, 
  BarChart3, 
  Compass, 
  LogOut,
  Palette,
  UserPlus,
  CreditCard,
  Wallet,
  LayoutDashboard,
  Sparkles,
  Megaphone
} from 'lucide-react';
import './SideNavMenu.css';

type SideNavMenuProps = {
  minifyNav:boolean;
  mobileNavOpen:boolean
  setMobileNavBarOpen: (value: boolean) => void;
}

const SideNavMenu = (props: SideNavMenuProps) => {
  const {minifyNav,mobileNavOpen,setMobileNavBarOpen}=props
  const location=useLocation()
  const [isHovered, setIsHovered] = useState(false)
  const { isNightMode, toggleNightMode } = useNightMode();

  const { user } = useAuth();
  const { entitlements } = user || {};
  const isPayoutEnabled = entitlements && entitlements.length > 0 && entitlements?.includes("payout");

  let isEventSecondAdmin = false;
  let isCoOrganizer = false;
  let userRole = "";
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsedData = JSON.parse(authUser);
      const role = String(parsedData.user?.userRole);
      isEventSecondAdmin = role === "8";
      isCoOrganizer = role === "10";
      userRole = role;
    }
  } catch (err) {
    console.error("Error parsing authUser", err);
  }

  const { setNavTitle } = useNavTitle();

  const navItems: { path: string; title: string; search?: string; exact?: boolean }[] = [
    { path: '/home', title: 'Dashboard', exact: true },
    { path: '/events', title: 'Events', exact: true },
    { path: '/create-event-v3', title: 'Create Event V3', exact: true },
    { path: '/create-quick-event', title: 'Create Event', exact: true },
    { path: '/create-quick-event', search: '?freeBooking=true', title: 'Create Free Event', exact: true },
    { path: '/create-quick-event-v2', title: 'Create Event', exact: true },
    { path: '/create-quick-event-v2', search: '?freeBooking=true', title: 'Create Free Event', exact: true },
    { path: '/contact-lists', title: 'Contact List' },
    { path: '/add-new-sub-user', title: 'Add Sub User' },
    { path: '/payouts', title: 'Payouts', exact: true },
    { path: '/promoters', title: 'Promoters', exact: true },
    { path: '/reports', title: 'Detailed Reports' },
    { path: '/discover', title: 'Discover', exact: true },
    { path: '/ai', title: 'Ask Ev', exact: true },
    { path: '/payout-request', title: 'Payout Request', exact: true },
    { path: '/profile', title: 'My Profile', exact: true },
  ];

  useEffect(() => {
    const match = navItems.find(item =>
      item.exact
        ? location.pathname === item.path && (item.search ?? '') === location.search
        : location.pathname.startsWith(item.path)
    );
    setNavTitle(match?.title ?? 'Dashboard');
  }, [location.pathname, location.search]);

  useEffect(() => {
     let links = document.querySelectorAll('.menu--link')
     links.forEach((el) => {
       const href = el.getAttribute('href');
       if (!href) return;

       const linkUrl = new URL(href, window.location.origin);
       const isCreateEventRoute = (pathname: string) =>
         pathname === '/create-quick-event' || pathname === '/create-quick-event-v2';
       const isActive = (
         (isCreateEventRoute(linkUrl.pathname) && isCreateEventRoute(location.pathname)
           || linkUrl.pathname === location.pathname)
         && linkUrl.search === location.search
       );

       if (isActive) {
         el.classList.add('active')
       }else{
        el.classList.remove('active')
       }
     })
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!mobileNavOpen) return;
    // Close only when the page itself scrolls — not when scrolling the sidebar list.
    const handleClose = (event: Event) => {
      const target = event.target as Node | null;
      const nav = document.querySelector(".vertical_nav.vertical_nav__opened");
      if (nav && target && nav.contains(target)) return;
      setMobileNavBarOpen(false);
    };
    window.addEventListener("scroll", handleClose, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", handleClose, true);
    };
  }, [mobileNavOpen, setMobileNavBarOpen]);
  
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const onClickLogout=()=>{
    localStorage.removeItem('token')
    window.location.href='/login'
  }

  const effectiveMinify = isDesktop ? true : minifyNav;
  const isExpanded = !effectiveMinify || isHovered;
  const profileName = user?.name || 'Organizer';
 
  return (
    <nav
      className={`vertical_nav ${effectiveMinify ? "vertical_nav__minify" : ""} ${
        mobileNavOpen ? "vertical_nav__opened" : ""
      } ${isHovered ? "sidebar-hovered" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top Brand Logo Section */}
      <Link to="/home" className="sidebar-logo-section">
        {/* Full Expanded Logo */}
        <img 
          src={isNightMode ? images.eventox_dark_logo : images.eventox_light_logo} 
          alt="Eventox Logo" 
          className="logo-expanded" 
          style={{
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.5)',
            pointerEvents: isExpanded ? 'auto' : 'none'
          }}
        />

        {/* Collapsed Icon/Logo */}
        <img 
          src={isNightMode ? images.white_icon : images.grey_logo} 
          alt="Eventox Collapsed Logo" 
          className="logo-collapsed" 
          style={{
            opacity: isExpanded ? 0 : 1,
            transform: isExpanded ? 'translate(-50%, -50%) scale(0.5)' : 'translate(-50%, -50%) scale(1)',
            pointerEvents: isExpanded ? 'none' : 'auto'
          }}
        />
      </Link>

      {/* Menu Items */}
      <div className="sidebar-nav-list">
        <Link
          to="/home"
          className="sidebar-btn menu--link"
          title="Dashboard"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <LayoutDashboard className="sidebar-icon" />
          <span className="sidebar-label">Dashboard</span>
        </Link>

        <Link
          to="/events"
          className="sidebar-btn menu--link"
          title="Events"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Calendar className="sidebar-icon" />
          <span className="sidebar-label">Events</span>
        </Link>
        

        {!isEventSecondAdmin && (
          <>
            {!isCoOrganizer && (
              <>
                {/* <Link
                  to="/create-event-v3"
                  className="sidebar-btn menu--link"
                  title="Create Event V3"
                  onClick={() => {
                    setMobileNavBarOpen(false);
                  }}
                >
                  <div className="active-indicator" />
                  <PlusCircle className="sidebar-icon text-pink" />
                  <span className="sidebar-label">Create Event V3</span>
                </Link> */}

                <Link
                  to="/create-quick-event"
                  className="sidebar-btn menu--link"
                  title="Create Events"
                  onClick={() => {
                    setMobileNavBarOpen(false);
                  }}
                >
                  <div className="active-indicator" />
                  <PlusCircle className="sidebar-icon" />
                  <span className="sidebar-label">Create Event</span>
                </Link>

                <Link
                  to="/create-quick-event?freeBooking=true"
                  className="sidebar-btn menu--link"
                  title="Create Free Event"
                  onClick={() => {
                    setMobileNavBarOpen(false);
                  }}
                >
                  <div className="active-indicator" />
                  <PlusCircle className="sidebar-icon" />
                  <span className="sidebar-label">Create Free Event</span>
                </Link>
              </>
            )}
            
            <Link
              to={`/contact-lists`}
              className="sidebar-btn menu--link"
              title="Contact List"
              onClick={() => {
                setMobileNavBarOpen(false);
              }}
            >
              <div className="active-indicator" />
              <Users className="sidebar-icon" />
              <span className="sidebar-label">Contact List</span>
            </Link>

            {!isCoOrganizer && (
              <Link to={"/add-new-sub-user"}
                className="sidebar-btn menu--link"
                title="Add Sub User"
                onClick={() => {
                  setMobileNavBarOpen(false);
                }}
              >
                <div className="active-indicator" />
                <UserPlus className="sidebar-icon" />
                <span className="sidebar-label">Add Sub User</span>
              </Link>
            )}

            {isPayoutEnabled && (
              <Link to={"/payouts"}
                className="sidebar-btn menu--link"
                title="Payouts"
                onClick={() => {
                  setMobileNavBarOpen(false);
                }}
              >
                <div className="active-indicator" />
                <CreditCard className="sidebar-icon" />
                <span className="sidebar-label">Payouts</span>
              </Link>
            )}

            <Link
              to={"/promoters"}
              className="sidebar-btn menu--link"
              title="Promoters"
              onClick={() => {
                setMobileNavBarOpen(false);
              }}
            >
              <div className="active-indicator" />
              <Megaphone className="sidebar-icon" />
              <span className="sidebar-label">Promoters</span>
            </Link>
            
            <Link
              to="/reports/run-report"
              className="sidebar-btn menu--link"
              title="Detailed Reports"
              onClick={() => {
                setMobileNavBarOpen(false);
              }}
            >
              <div className="active-indicator" />
              <BarChart3 className="sidebar-icon" />
              <span className="sidebar-label">Detailed Reports</span>
            </Link>

            <Link
              to="/discover"
              className="sidebar-btn menu--link"
              title="Discover"
              onClick={() => {
                setMobileNavBarOpen(false);
              }}
            >
              <div className="active-indicator" />
              <Compass className="sidebar-icon" />
              <span className="sidebar-label">Discover</span>
            </Link>

            <Link
              to="/ai"
              className="sidebar-btn menu--link"
              title="Ask Ev"
              onClick={() => {
                setMobileNavBarOpen(false);
              }}
            >
              <div className="active-indicator" />
              <Sparkles className="sidebar-icon" />
              <span className="sidebar-label">Ask Ev</span>
            </Link>

            {isPayoutEnabled && (
              <Link
                to="/payout-request"
                className="sidebar-btn menu--link"
                title="Payout Request"
                onClick={() => {
                  setMobileNavBarOpen(false);
                }}
              >
                <div className="active-indicator" />
                <Wallet className="sidebar-icon" />
                <span className="sidebar-label">Payout</span>
              </Link>
            )}
          </>
        )}
      </div>

      {/* Bottom Actions - Logout, Theme & Identity */}
      <div className="sidebar-bottom-actions">
        {/* Adaptive Theme Toggle Option */}
        <button
          id="sidebar-theme-toggle"
          onClick={toggleNightMode}
          className="sidebar-btn"
          title="Switch App Theme"
        >
          <Palette className={`sidebar-icon ${isNightMode ? 'theme-pulse-icon' : ''}`} />
          <div className="profile-info">
            <span className="sub-meta-text">App Theme</span>
            <span className="theme-name">
              {isNightMode ? 'Eventox Original' : 'Classic Gray'}
            </span>
          </div>
        </button>

        {/* Logout Link */}
        <Link
          to="/login"
          className="sidebar-btn logout-btn menu--link"
          title="Logout"
          onClick={() => {
            onClickLogout(); 
            setMobileNavBarOpen(false);
            localStorage.clear();
          }}
        >
          <LogOut className="sidebar-icon logout-icon" />
          <span className="sidebar-label">Logout</span>
        </Link>

        {/* Organizer mini identity */}
        <Link
          to="/profile"
          id="sidebar-mini-profile"
          className="sidebar-mini-profile"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="profile-avatar">
            {profileName.split(' ').map(n => n[0]).join('') || 'O'}
          </div>
          <div className="profile-info">
            <span className="profile-name">{profileName}</span>
            <span className="profile-role">
              {userRole === "8"
                ? "Event co organizer"
                : userRole === "10"
                ? "Co organizer"
                : "Organizer"}
            </span>
          </div>
        </Link>
      </div>
    </nav>
  );
}

export default SideNavMenu